import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getScenarios, runTestCase } from '../api/testcases'
import type { Scenario, TestCase } from '../types'
import { useStore } from '../store'

interface Props {
  active: boolean
  onNavigate: () => void
}

function DocBadge({ label, active }: { label: string; active: boolean }) {
  if (!active) return null
  return (
    <span className="text-[10px] font-mono px-1 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 leading-none">
      {label}
    </span>
  )
}

export function ScenarioDropdownBtn({ active, onNavigate }: Props) {
  const { t } = useTranslation()
  const { setCurrentSession, setLoadedTestCase, setActiveView, setError } = useStore()

  const [open, setOpen]           = useState(false)
  const [scenarios, setScenarios] = useState<Scenario[] | null>(null)
  const [loading, setLoading]     = useState(false)
  const [runningId, setRunningId] = useState<number | null>(null)

  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchIfNeeded = async () => {
    if (scenarios !== null) return
    setLoading(true)
    try {
      setScenarios(await getScenarios())
    } catch {
      setScenarios([])
    } finally {
      setLoading(false)
    }
  }

  const handleMouseEnter = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current)
    setOpen(true)
    fetchIfNeeded()
  }

  const handleMouseLeave = () => {
    hideTimer.current = setTimeout(() => setOpen(false), 150)
  }

  const handleRun = async (scenario: Scenario, tc: TestCase) => {
    setOpen(false)
    setRunningId(tc.id)
    try {
      const session = await runTestCase(tc.id)
      setCurrentSession(session)
      setLoadedTestCase({ scenarioName: scenario.name, testCaseName: tc.name })
      setActiveView('result')
    } catch (e) {
      setError(e instanceof Error ? e.message : t('scenario_dropdown.error_run'))
    } finally {
      setRunningId(null)
    }
  }

  const totalCases = scenarios?.reduce((n, s) => n + s.cases.length, 0) ?? 0

  return (
    <div
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        onClick={onNavigate}
        className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
          active
            ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm border border-gray-200 dark:border-gray-600'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-white/70 dark:hover:bg-gray-700/70'
        }`}
      >
        {t('scenario_dropdown.btn_label')}
        {totalCases > 0 && (
          <span className="ml-1.5 text-[10px] text-gray-400 dark:text-gray-500 tabular-nums">
            {totalCases}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 w-max min-w-[240px] max-w-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden">

          <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              {t('scenario_dropdown.header')}
            </span>
            <button
              onClick={onNavigate}
              className="text-xs text-blue-500 hover:text-blue-700 dark:text-blue-400"
            >
              {t('scenario_dropdown.manage_link')}
            </button>
          </div>

          <div className="overflow-y-auto max-h-80">
            {loading && (
              <div className="px-3 py-4 text-xs text-gray-400 text-center">{t('common.loading')}</div>
            )}
            {!loading && scenarios?.length === 0 && (
              <div className="px-3 py-4 text-xs text-gray-400 text-center italic">
                {t('scenario_dropdown.no_scenarios')}
              </div>
            )}
            {!loading && scenarios && scenarios.map(scenario => (
              <div key={scenario.id}>
                <div className="px-3 pt-2.5 pb-1">
                  <span className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                    {scenario.name}
                  </span>
                </div>

                {scenario.cases.length === 0 && (
                  <div className="px-3 pb-2 text-xs text-gray-300 dark:text-gray-600 italic">
                    {t('scenario_dropdown.no_cases')}
                  </div>
                )}
                {scenario.cases.map(tc => (
                  <button
                    key={tc.id}
                    onClick={() => handleRun(scenario, tc)}
                    disabled={runningId !== null}
                    className="w-full text-left px-3 py-1.5 flex items-center gap-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors disabled:opacity-50"
                  >
                    {runningId === tc.id ? (
                      <span className="text-xs text-blue-500 animate-pulse">{t('common.loading')}</span>
                    ) : (
                      <>
                        <span className="flex-1 text-xs text-gray-700 dark:text-gray-300 truncate">
                          {tc.name}
                        </span>
                        <span className="flex items-center gap-0.5 shrink-0">
                          <DocBadge label="OR" active={tc.has_ordrsp} />
                          <DocBadge label="DA" active={tc.has_desadv} />
                          <DocBadge label="INV" active={tc.has_invoice} />
                        </span>
                      </>
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
